"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
	AlertTriangle,
	CheckCircle2,
	Download,
	Square,
	Eye,
	EyeOff,
	Loader2,
	RefreshCw,
	Stethoscope,
} from "lucide-react";
import {
	hocphiApi,
	type HocPhiConfig,
	type HocPhiFailureKind,
	type HocPhiLastSync,
	type HocPhiProbeResult,
	type HocPhiRunFile,
	type HocPhiSchedule,
	type HocPhiSyncStatus,
} from "@/lib/hocphi-api.service";
import { useHocPhiProgressEvents } from "@/hooks/useEventStream";
import { Toast, getSafeErrorMessage } from "@/lib/utils";

/**
 * Safety net only. Progress arrives over SSE; this poll exists because a run
 * started from the CLI lives in another process and cannot emit SSE at all, and
 * because a dropped stream should not freeze the bar forever.
 */
const STATUS_POLL_MS = 15000;

function formatTime(iso: string): string {
	return new Date(iso).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
}

/** Clock only. Used beside a full date that already says which day it was. */
function formatClock(value: string | number): string {
	return new Date(value).toLocaleTimeString("vi-VN", {
		timeZone: "Asia/Ho_Chi_Minh",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** "45 giây" / "12 phút" / "1 giờ 20 phút" — the coarsest unit that still reads true. */
function formatDuration(ms: number): string {
	const totalMinutes = Math.floor(ms / 60000);
	if (totalMinutes < 1) {
		return `${Math.max(0, Math.round(ms / 1000))} giây`;
	}

	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return hours > 0 ? `${hours} giờ ${minutes} phút` : `${totalMinutes} phút`;
}

/**
 * Wall clock that ticks once a second while `active`, so an elapsed counter keeps
 * moving between progress events instead of freezing until the next one lands.
 */
function useNow(active: boolean): number {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!active) return;

		setNow(Date.now());
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [active]);

	return now;
}

/**
 * Projected finish, straight-lined from the rate so far. Each student costs one
 * upstream lookup, so the average per student is a fair basis; before the first
 * batch reports there is nothing to project from and this returns null rather
 * than inventing a time.
 */
function estimateFinish(lastRun: HocPhiLastSync | null | undefined, now: number): number | null {
	const processed = lastRun?.processed ?? 0;
	const total = lastRun?.total ?? 0;
	if (!lastRun || processed <= 0 || total <= processed) {
		return null;
	}

	const started = new Date(lastRun.startedAt).getTime();
	const elapsed = now - started;
	if (!Number.isFinite(started) || elapsed <= 0) {
		return null;
	}

	return started + (elapsed / processed) * total;
}

const TRIGGER_LABEL: Record<string, string> = {
	manual: "chạy tay",
	cron: "tự động",
	cli: "dòng lệnh",
};

/**
 * Ready-made schedules, so setting one never requires knowing cron syntax.
 * The free-text field stays for anything not covered here.
 */
const CRON_PRESETS: Array<{ label: string; expression: string }> = [
	{ label: "Mỗi giờ", expression: "0 * * * *" },
	{ label: "Mỗi 2 giờ", expression: "0 */2 * * *" },
	{ label: "Mỗi 3 giờ", expression: "0 */3 * * *" },
	{ label: "Mỗi 6 giờ", expression: "0 */6 * * *" },
	{ label: "Hằng ngày 0:30", expression: "30 0 * * *" },
	{ label: "Hằng ngày 12:30", expression: "30 12 * * *" },
	{ label: "Thứ 2 hằng tuần, 1:00", expression: "0 1 * * 1" },
];

/** Plain-language reading of an expression, for the presets and common shapes. */
function describeCron(expression: string): string | null {
	const preset = CRON_PRESETS.find((item) => item.expression === expression.trim());
	if (preset) {
		return preset.label.toLowerCase();
	}

	const [minute, hour, dayOfMonth, month, dayOfWeek] = expression.trim().split(/\s+/);
	if (!dayOfWeek || dayOfMonth !== "*" || month !== "*" || dayOfWeek !== "*") {
		return null;
	}
	if (hour === "*" && /^\d+$/.test(minute)) {
		return `mỗi giờ, vào phút thứ ${minute}`;
	}
	const everyNHours = /^\*\/(\d+)$/.exec(hour ?? "");
	if (everyNHours && /^\d+$/.test(minute)) {
		return `mỗi ${everyNHours[1]} giờ, vào phút thứ ${minute}`;
	}
	if (/^\d+$/.test(hour) && /^\d+$/.test(minute)) {
		return `hằng ngày lúc ${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
	}
	return null;
}

/**
 * Admin editor for the tuition-fee integration: upstream credentials, the
 * automatic schedule, and a manual refresh.
 *
 * The scheduler runs inside the API process, so a schedule change here takes
 * effect immediately — there is no separate container to restart.
 */
export default function HocPhiConfigManagement() {
	const [config, setConfig] = useState<HocPhiConfig | null>(null);
	const [schedule, setSchedule] = useState<HocPhiSchedule | null>(null);
	const [status, setStatus] = useState<HocPhiSyncStatus | null>(null);
	const [loading, setLoading] = useState(true);

	const [showPassword, setShowPassword] = useState(false);
	const [savingConfig, setSavingConfig] = useState(false);
	const [savingEnabled, setSavingEnabled] = useState(false);
	const [savingSchedule, setSavingSchedule] = useState(false);
	const [triggering, setTriggering] = useState(false);
	const [scheduleError, setScheduleError] = useState<string | null>(null);

	const [cancelling, setCancelling] = useState(false);
	const [runs, setRuns] = useState<HocPhiRunFile[]>([]);
	const [probeMssv, setProbeMssv] = useState("");
	const [probing, setProbing] = useState(false);
	const [probeResult, setProbeResult] = useState<HocPhiProbeResult | null>(null);
	const [savingProbe, setSavingProbe] = useState(false);

	// Previous value of `status.running`, so the effect below can spot the moment
	// a run ends. A ref rather than state: it must not itself cause a render.
	const wasRunning = useRef(false);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const [nextConfig, nextSchedule, nextStatus, nextRuns] = await Promise.all([
					hocphiApi.getConfig(),
					hocphiApi.getSchedule(),
					hocphiApi.getSyncStatus(),
					hocphiApi.listRuns().catch(() => []),
				]);
				if (cancelled) return;
				setConfig(nextConfig);
				setSchedule(nextSchedule);
				setStatus(nextStatus);
				setRuns(nextRuns);
			} catch (error) {
				if (!cancelled) Toast.error(getSafeErrorMessage(error));
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	// Live progress. Merged into the same `status` shape the poll produces, so the
	// bar has one source of truth no matter which channel delivered the numbers.
	useHocPhiProgressEvents(
		useCallback((progress) => {
			if (!progress.running) {
				// The run ended. Only the server knows how it finished, so fetch the
				// outcome instead of guessing it from the event. Flipping `running`
				// to false is what drives the completion effect below — the run log
				// is refreshed there, not here, so SSE and the poll end a run the
				// same way.
				void hocphiApi
					.getSyncStatus()
					.then(setStatus)
					.catch(() => undefined);
				return;
			}

			setStatus((current) => {
				return {
					running: true,
					lastRun: {
						status: "running",
						trigger: progress.trigger ?? "manual",
						startedAt: current?.lastRun?.startedAt ?? progress.timestamp,
						processed: progress.processed,
						total: progress.total,
						successful: progress.successful,
						failed: progress.failed,
						paid: progress.paid,
						unpaid: progress.unpaid,
						cohortPrefix: progress.cohortPrefix,
					},
				};
			});
		}, []),
	);

	// Polls only while a run is in flight, including one already running when the
	// page opened (started by another admin, or by the schedule).
	useEffect(() => {
		if (!status?.running) return;

		const timer = setInterval(async () => {
			try {
				setStatus(await hocphiApi.getSyncStatus());

				// Also refresh the log list, so the run in flight shows up there with
				// its elapsed time instead of appearing only once it has finished.
				void hocphiApi
					.listRuns()
					.then(setRuns)
					.catch(() => undefined);
			} catch {
				// A single failed poll is not worth a toast; the next tick retries.
			}
		}, STATUS_POLL_MS);

		return () => clearInterval(timer);
	}, [status?.running]);

	// Everything that happens when a run ends, in one place.
	//
	// Keyed on the `running` transition rather than on the channel that reported
	// it: SSE and the poll race to notice the same event, and whichever loses
	// used to skip both the toast and the log refresh — which is why the log only
	// caught up on a page reload.
	//
	// `status` in the deps means this re-runs on every progress tick, but the
	// transition guard makes all of those a no-op.
	useEffect(() => {
		const running = status?.running ?? false;
		const justFinished = wasRunning.current && !running;
		wasRunning.current = running;

		if (!justFinished) return;

		// The final rows land in the file before the run is reported finished, so
		// this fetch always sees the completed log.
		void hocphiApi
			.listRuns()
			.then(setRuns)
			.catch(() => undefined);

		if (status?.lastRun?.status === "error") {
			Toast.error("Cập nhật học phí thất bại", status.lastRun.message);
		} else {
			Toast.success("Cập nhật học phí hoàn tất", status?.lastRun?.message);
		}
	}, [status]);

	const handleSaveConfig = useCallback(async () => {
		if (!config) return;

		setSavingConfig(true);
		try {
			setConfig(
				await hocphiApi.updateConfig({
					baseUrl: config.baseUrl,
					username: config.username,
					password: config.password,
				}),
			);
			Toast.success("Đã lưu cấu hình học phí");
		} catch (error) {
			Toast.error(getSafeErrorMessage(error));
		} finally {
			setSavingConfig(false);
		}
	}, [config]);

	const handleSaveSchedule = useCallback(async (next: HocPhiSchedule) => {
		setSavingSchedule(true);
		setScheduleError(null);
		try {
			setSchedule(await hocphiApi.updateSchedule(next));
			Toast.success(next.enabled ? "Đã bật lịch tự động" : "Đã tắt lịch tự động");
		} catch (error) {
			// Shown inline under the input rather than only as a toast: the fix
			// belongs at the field the admin has to correct.
			setScheduleError(getSafeErrorMessage(error));
		} finally {
			setSavingSchedule(false);
		}
	}, []);

	const handleProbe = useCallback(async () => {
		const mssv = probeMssv.trim();
		if (!mssv) return;

		setProbing(true);
		setProbeResult(null);
		try {
			// A failed lookup is a successful test — the answer is the diagnosis,
			// so it renders inline rather than as an error toast.
			setProbeResult(await hocphiApi.probe(mssv));
		} catch (error) {
			Toast.error(getSafeErrorMessage(error));
		} finally {
			setProbing(false);
		}
	}, [probeMssv]);

	const handleCancel = useCallback(async () => {
		setCancelling(true);
		try {
			await hocphiApi.cancelSync();
			Toast.success("Đã yêu cầu dừng", "Lượt chạy sẽ kết thúc trong giây lát");
		} catch (error) {
			Toast.error(getSafeErrorMessage(error));
		} finally {
			setCancelling(false);
		}
	}, []);

	const handleApplyProbe = useCallback(async () => {
		if (!probeResult?.ok) return;

		setSavingProbe(true);
		try {
			const applied = await hocphiApi.applyProbe(probeResult.mssv);
			setProbeResult(null);
			setProbeMssv("");
			if (applied.ok) {
				Toast.success(
					"Đã lưu",
					`${applied.mssv} được đặt ${applied.feeStatus === "PAID" ? "đã đóng" : "còn nợ"}`,
				);
			} else {
				Toast.error("Không lưu được", "Tra cứu lại lúc lưu bị lỗi, học phí giữ nguyên");
			}
		} catch (error) {
			Toast.error(getSafeErrorMessage(error));
		} finally {
			setSavingProbe(false);
		}
	}, [probeResult]);

	const handleDismissProbe = useCallback(() => {
		setProbeResult(null);
		setProbeMssv("");
	}, []);

	/**
	 * Master switch. Applied immediately rather than through "Lưu cấu hình": a
	 * toggle that needs a second, separate save reads as broken.
	 */
	const handleToggleEnabled = useCallback(async (enabled: boolean) => {
		// Optimistic, so the switch moves on the click instead of after the round
		// trip. Rolled back below if the server refuses.
		setConfig((current) => (current ? { ...current, enabled } : current));
		setSavingEnabled(true);
		try {
			setConfig(await hocphiApi.setEnabled(enabled));
			Toast.success(enabled ? "Đã bật tích hợp học phí" : "Đã tắt tích hợp học phí");
		} catch (error) {
			setConfig((current) => (current ? { ...current, enabled: !enabled } : current));
			Toast.error(getSafeErrorMessage(error));
		} finally {
			setSavingEnabled(false);
		}
	}, []);

	const handleTrigger = useCallback(async () => {
		setTriggering(true);
		try {
			setStatus(await hocphiApi.triggerSync());
			Toast.success("Đã bắt đầu cập nhật học phí");
		} catch (error) {
			Toast.error(getSafeErrorMessage(error));
		} finally {
			setTriggering(false);
		}
	}, []);

	if (loading || !config || !schedule) {
		return (
			<Card>
				<CardContent className="p-4 space-y-4">
					<Skeleton className="h-5 w-48" />
					<Skeleton className="h-9 w-full" />
					<div className="grid gap-4 sm:grid-cols-2">
						<Skeleton className="h-9 w-full" />
						<Skeleton className="h-9 w-full" />
					</div>
					<Skeleton className="h-9 w-40" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="p-4 space-y-6">
				<section className="space-y-3">
					<div className="flex items-start justify-between gap-3">
						<div className="space-y-1">
							<div className="flex items-center gap-2">
								<h3 className="font-medium text-foreground">Kết nối hệ thống học phí</h3>
								{!config.enabled && (
									<Badge variant="outline" className="text-amber-700 dark:text-amber-400">
										Đang tắt
									</Badge>
								)}
							</div>
							<p className="text-sm text-muted-foreground">
								{config.source === "env"
									? "Đang dùng giá trị từ .env. Nhập và lưu tại đây để chuyển sang cấu hình trong cơ sở dữ liệu."
									: "Đang dùng cấu hình lưu trong cơ sở dữ liệu."}
							</p>
						</div>
						<div className="flex items-center gap-2 shrink-0">
							{savingEnabled && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
							<label htmlFor="hocphi-enabled" className="text-sm text-muted-foreground">
								{config.enabled ? "Bật" : "Tắt"}
							</label>
							<Switch
								id="hocphi-enabled"
								checked={config.enabled}
								disabled={savingEnabled}
								onCheckedChange={handleToggleEnabled}
							/>
						</div>
					</div>

					{!config.enabled && (
						<p className="text-sm text-muted-foreground">
							Khi tắt, hệ thống không gọi sang hệ thống học phí: bỏ qua lượt chạy theo lịch, từ chối
							cập nhật thủ công và tra cứu thử. Số liệu học phí đã lưu giữ nguyên.
						</p>
					)}

					{config.passwordUnreadable && (
						<p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
							<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
							Mật khẩu đã lưu không đọc được (khoá mã hoá đã thay đổi). Nhập lại rồi lưu.
						</p>
					)}

					<div className="space-y-1">
						<label htmlFor="hocphi-base-url" className="text-sm font-medium text-foreground">
							Địa chỉ API
						</label>
						<Input
							id="hocphi-base-url"
							value={config.baseUrl}
							placeholder="http://<máy-chủ>:9101/api/bsc"
							onChange={(event) => setConfig({ ...config, baseUrl: event.target.value })}
						/>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1">
							<label htmlFor="hocphi-username" className="text-sm font-medium text-foreground">
								Tài khoản
							</label>
							<Input
								id="hocphi-username"
								value={config.username}
								onChange={(event) => setConfig({ ...config, username: event.target.value })}
							/>
						</div>

						<div className="space-y-1">
							<label htmlFor="hocphi-password" className="text-sm font-medium text-foreground">
								Mật khẩu
							</label>
							<div className="flex items-center gap-2">
								<Input
									id="hocphi-password"
									type={showPassword ? "text" : "password"}
									value={config.password}
									onChange={(event) => setConfig({ ...config, password: event.target.value })}
								/>
								<Button
									type="button"
									variant="outline"
									size="icon"
									className="h-9 w-9 shrink-0"
									aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
									onClick={() => setShowPassword((value) => !value)}
								>
									{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground">
								Để trống rồi lưu sẽ quay về giá trị trong .env, không phải tắt tính năng.
							</p>
						</div>
					</div>

					<Button onClick={handleSaveConfig} disabled={savingConfig} className="h-9">
						{savingConfig && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
						{savingConfig ? "Đang lưu..." : "Lưu cấu hình"}
					</Button>

					{/* A two-second answer to "does this work at all", against one
						record instead of thousands. Reads the SAVED configuration. */}
					<div className="border-t pt-3 space-y-2">
						<div className="flex items-center gap-2 flex-wrap">
							<Input
								aria-label="MSSV cần tra cứu thử"
								className="max-w-40 font-mono"
								placeholder="25180140"
								value={probeMssv}
								disabled={probing || !config.enabled}
								onChange={(event) => setProbeMssv(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") handleProbe();
								}}
							/>
							<Button
								variant="outline"
								className="h-9"
								disabled={probing || !probeMssv.trim() || !config.enabled}
								onClick={handleProbe}
							>
								{probing ? (
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
								) : (
									<Stethoscope className="h-4 w-4 mr-2" />
								)}
								{probing ? "Đang tra cứu..." : "Tra cứu thử"}
							</Button>
							<span className="text-xs text-muted-foreground">
								Dùng cấu hình đã lưu. Chỉ đọc — không đổi học phí của sinh viên nào.
							</span>
						</div>

						{probeResult && (
							<div className="space-y-2">
								<ProbeOutcome result={probeResult} />

								{/* Offered only on a successful lookup: there is nothing to save
								when the upstream did not answer. */}
								{probeResult.ok && (
									<div className="flex items-center gap-2">
										<Button
											size="sm"
											className="h-8"
											disabled={savingProbe}
											onClick={handleApplyProbe}
										>
											{savingProbe && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
											{savingProbe ? "Đang lưu..." : "Lưu kết quả"}
										</Button>
										<Button
											size="sm"
											variant="outline"
											className="h-8"
											disabled={savingProbe}
											onClick={handleDismissProbe}
										>
											Bỏ qua
										</Button>
										<span className="text-xs text-muted-foreground">
											Lưu sẽ tra cứu lại một lần nữa rồi mới ghi, để không ghi nhầm số liệu cũ.
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				</section>

				<section className="space-y-3 border-t pt-4">
					<div className="flex items-center justify-between gap-3">
						<div className="space-y-1">
							<label htmlFor="hocphi-schedule-enabled" className="font-medium text-foreground">
								Tự động cập nhật theo lịch
							</label>
							<p className="text-sm text-muted-foreground">
								Khi bật, hệ thống tự cập nhật học phí theo biểu thức lịch bên dưới.
							</p>
						</div>
						<div className="flex items-center gap-2">
							{savingSchedule && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
							<Switch
								id="hocphi-schedule-enabled"
								checked={schedule.enabled}
								disabled={savingSchedule}
								onCheckedChange={(enabled) => handleSaveSchedule({ ...schedule, enabled })}
							/>
						</div>
					</div>

					{/* Presets first: picking one is the normal path, and it means an
						admin never has to remember the five cron fields. */}
					<div className="flex items-center gap-2 flex-wrap">
						{CRON_PRESETS.map((preset) => (
							<Button
								key={preset.expression}
								variant={schedule.cronExpression === preset.expression ? "default" : "outline"}
								size="sm"
								className="h-8"
								disabled={savingSchedule}
								onClick={() =>
									handleSaveSchedule({ ...schedule, cronExpression: preset.expression })
								}
							>
								{preset.label}
							</Button>
						))}
					</div>

					<div className="flex items-center gap-2 flex-wrap">
						<Input
							aria-label="Biểu thức lịch"
							className="max-w-56 font-mono"
							value={schedule.cronExpression}
							disabled={savingSchedule}
							onChange={(event) => setSchedule({ ...schedule, cronExpression: event.target.value })}
						/>
						<Button
							variant="outline"
							className="h-9"
							disabled={savingSchedule}
							onClick={() => handleSaveSchedule(schedule)}
						>
							Lưu lịch
						</Button>
						<span className="text-xs text-muted-foreground">
							{describeCron(schedule.cronExpression)
								? `Chạy ${describeCron(schedule.cronExpression)} (giờ Việt Nam)`
								: "Định dạng: phút giờ ngày tháng thứ — giờ Việt Nam"}
						</span>
					</div>

					{scheduleError && (
						<p className="text-sm text-red-600 dark:text-red-400">{scheduleError}</p>
					)}
				</section>

				<section className="space-y-3 border-t pt-4">
					<div className="flex items-center gap-3 flex-wrap">
						<Button
							onClick={handleTrigger}
							disabled={triggering || status?.running || !config.enabled}
							className="h-9"
						>
							{triggering || status?.running ? (
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
							) : (
								<RefreshCw className="h-4 w-4 mr-2" />
							)}
							{status?.running ? "Đang cập nhật..." : "Cập nhật học phí ngay"}
						</Button>

						{status?.running && (
							<Button
								variant="destructive"
								className="h-9"
								disabled={cancelling}
								onClick={handleCancel}
							>
								{cancelling ? (
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
								) : (
									<Square className="h-4 w-4 mr-2" />
								)}
								{cancelling ? "Đang dừng..." : "Dừng"}
							</Button>
						)}

						<SyncBadge status={status} />
					</div>

					{/* A second full run while one is in flight would double the load on an
						upstream that is already the bottleneck, so the button stays locked.
						Say why, and point at the one lookup that still works. */}
					{status?.running && (
						<p className="text-sm text-amber-700 dark:text-amber-400">
							{status.lastRun?.trigger === "cron"
								? "Đang chạy theo lịch tự động — không thể chạy thêm một lượt đầy đủ lúc này."
								: "Đang chạy một lượt đầy đủ — không thể bắt đầu lượt thứ hai."}{" "}
							Vẫn tra cứu được từng MSSV bằng "Tra cứu thử" ở phần Kết nối phía trên.
						</p>
					)}

					{status?.running && <SyncProgress lastRun={status.lastRun} />}

					{status?.lastRun && !status.running && <SyncOutcome lastRun={status.lastRun} />}

					<RunLogList runs={runs} lastRun={status?.running ? (status.lastRun ?? null) : null} />
				</section>
			</CardContent>
		</Card>
	);
}

/**
 * What each failure kind means and what to do about it. An operator seeing a
 * bare count cannot tell a dead VPN from a wrong password from students the fee
 * system simply does not list — three problems with three different owners.
 */
const FAILURE_INFO: Record<HocPhiFailureKind, { label: string; hint: string; severe: boolean }> = {
	notFound: {
		label: "không có trong hệ thống học phí",
		hint: "Bình thường với sinh viên chưa phát sinh học phí. Không cần xử lý.",
		severe: false,
	},
	auth: {
		label: "sai thông tin đăng nhập",
		hint: "Nhập lại tài khoản và mật khẩu ở phần Kết nối phía trên.",
		severe: true,
	},
	connection: {
		label: "không kết nối được",
		hint: "Máy chủ học phí không phản hồi — kiểm tra VPN/Tailscale rồi chạy lại.",
		severe: true,
	},
	upstream: {
		label: "hệ thống học phí báo lỗi",
		hint: "Lỗi từ phía máy chủ trường. Thử lại sau.",
		severe: true,
	},
	unknown: {
		label: "lỗi không xác định",
		hint: "Xem log của máy chủ để biết chi tiết.",
		severe: true,
	},
};

/** How far the run in flight has got, and how long it has taken so far. */
function SyncProgress({ lastRun }: { lastRun: HocPhiSyncStatus["lastRun"] }) {
	const now = useNow(true);
	const processed = lastRun?.processed ?? 0;
	const total = lastRun?.total ?? 0;

	const startedMs = lastRun?.startedAt ? new Date(lastRun.startedAt).getTime() : NaN;
	const started = Number.isFinite(startedMs) ? startedMs : null;
	const eta = estimateFinish(lastRun, now);

	// A full run takes tens of minutes, so "how long has this been going and when
	// will it end" is the question asked more often than the percentage itself.
	const timing = (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground tabular-nums">
			{started !== null && <span>Bắt đầu {formatClock(started)}</span>}
			{started !== null && <span>Đã chạy {formatDuration(Math.max(0, now - started))}</span>}
			<span>
				{eta === null ? "Chưa ước tính được lúc xong" : `Dự kiến xong ${formatClock(eta)}`}
			</span>
		</div>
	);

	// No denominator yet — show the count but no bar. A bar with an invented
	// ratio is worse than no bar.
	if (!total) {
		return (
			<div className="space-y-1">
				<p className="text-sm text-muted-foreground tabular-nums">
					Đã xử lý {processed.toLocaleString("vi-VN")} sinh viên...
				</p>
				{timing}
			</div>
		);
	}

	const percent = Math.min(100, Math.round((processed / total) * 100));

	return (
		<div className="space-y-1">
			<Progress value={percent} />
			<div className="flex items-center justify-between text-sm text-muted-foreground tabular-nums">
				<span>
					{processed.toLocaleString("vi-VN")} / {total.toLocaleString("vi-VN")} sinh viên ·{" "}
					{percent}%{lastRun?.cohortPrefix && ` · khoá ${lastRun.cohortPrefix}`}
				</span>
				<span>
					{(lastRun?.paid ?? 0).toLocaleString("vi-VN")} đã đóng ·{" "}
					{(lastRun?.unpaid ?? 0).toLocaleString("vi-VN")} còn nợ
				</span>
			</div>
			{timing}
		</div>
	);
}

/**
 * What one run actually changed: the two update counts, the skips, the errors.
 *
 * The row count beside it does not answer what an operator asks — "5,000
 * students" says nothing about how many records changed. The numbers come from
 * the run's own CSV, so a run cancelled halfway still reports what it did.
 *
 * Zeroes are hidden: a clean run should not spend space saying "0 errors".
 */
function RunTally({ run }: { run: HocPhiRunFile }) {
	const parts: Array<{ key: string; label: string; className: string }> = [
		{
			key: "paid",
			label: `${run.paid.toLocaleString("vi-VN")} → đã đóng`,
			className: "text-green-700 dark:text-green-400",
		},
		{
			key: "unpaid",
			label: `${run.unpaid.toLocaleString("vi-VN")} → còn nợ`,
			className: "text-amber-700 dark:text-amber-400",
		},
		{
			key: "skipped",
			label: `${run.skipped.toLocaleString("vi-VN")} không đổi`,
			className: "text-muted-foreground",
		},
		{
			key: "failed",
			label: `${run.failed.toLocaleString("vi-VN")} lỗi`,
			className: "text-red-600 dark:text-red-400",
		},
	].filter(({ key }) => run[key as "paid" | "unpaid" | "skipped" | "failed"] > 0);

	if (parts.length === 0) {
		return <span className="text-muted-foreground">chưa cập nhật hồ sơ nào</span>;
	}

	return (
		<span className="flex flex-wrap items-center gap-x-2 tabular-nums">
			{parts.map(({ key, label, className }) => (
				<span key={key} className={className}>
					{label}
				</span>
			))}
		</span>
	);
}

/**
 * When each run started, when it ended, and how long that took.
 *
 * For the run still in flight the end time is a projection, not a fact, so it is
 * labelled as one — an operator planning around it must be able to tell the
 * difference.
 */
function RunTiming({
	run,
	activeRun,
	now,
}: {
	run: HocPhiRunFile;
	activeRun: HocPhiLastSync | null;
	now: number;
}) {
	const started = new Date(run.startedAt).getTime();

	if (activeRun) {
		const eta = estimateFinish(activeRun, now);
		return (
			<span className="text-muted-foreground tabular-nums">
				đang chạy · đã {formatDuration(Math.max(0, now - started))}
				{eta !== null && ` · dự kiến xong ${formatClock(eta)}`}
			</span>
		);
	}

	const finished = new Date(run.finishedAt).getTime();
	if (!Number.isFinite(finished) || finished <= started) {
		return null;
	}

	return (
		<span className="text-muted-foreground tabular-nums">
			xong {formatClock(finished)} · chạy {formatDuration(finished - started)}
		</span>
	);
}

/** Past runs, newest first, each downloadable as a per-student CSV. */
function RunLogList({ runs, lastRun }: { runs: HocPhiRunFile[]; lastRun: HocPhiLastSync | null }) {
	// Only ticks while a run is in flight; a list of finished runs is static.
	const now = useNow(lastRun !== null);

	if (runs.length === 0) {
		return null;
	}

	return (
		<div className="border-t pt-3 space-y-2">
			<h4 className="text-sm font-medium text-foreground">Nhật ký các lượt đã chạy</h4>
			<ul className="space-y-1">
				{runs.map((run) => (
					<li key={run.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
						<span className="text-muted-foreground tabular-nums">{formatTime(run.startedAt)}</span>
						<RunTiming
							run={run}
							activeRun={lastRun?.logFile === run.id ? lastRun : null}
							now={now}
						/>
						<span className="text-muted-foreground tabular-nums">
							{run.rows.toLocaleString("vi-VN")} sinh viên · {Math.round(run.bytes / 1024)} KB
						</span>
						<RunTally run={run} />
						{/* A plain link, not a fetch: the browser streams the file and
							shows its own download UI. */}
						<a
							href={hocphiApi.downloadRunUrl(run.id)}
							download={run.id}
							className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
						>
							<Download className="h-3 w-3" />
							Tải CSV
						</a>
					</li>
				))}
			</ul>
		</div>
	);
}

/** Result of a single test lookup, phrased as a diagnosis. */
function ProbeOutcome({ result }: { result: HocPhiProbeResult }) {
	if (result.ok) {
		return (
			<div className="flex items-start gap-2 text-sm text-green-700 dark:text-green-400">
				<CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
				<span>
					Kết nối tốt. <span className="font-mono">{result.mssv}</span>:{" "}
					{result.hasDebt ? "còn nợ học phí" : "đã đóng học phí"}
					{result.amountOwed !== null && result.amountOwed > 0 && (
						<> — {result.amountOwed.toLocaleString("vi-VN")} đ</>
					)}
					. Nếu chạy cập nhật, sinh viên này sẽ được đặt{" "}
					<span className="font-medium">{result.feeStatus}</span>.
				</span>
			</div>
		);
	}

	const info = FAILURE_INFO[result.kind];

	// "Not found" still proves the connection and the credentials work, which is
	// the opposite conclusion from every other failure kind.
	return (
		<div
			className={`flex items-start gap-2 text-sm ${info.severe ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}
		>
			<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
			<span>
				<span className="font-mono">{result.mssv}</span>: {info.label}. {info.hint}
				{result.kind === "notFound" && " Kết nối và tài khoản vẫn hoạt động bình thường."}
			</span>
		</div>
	);
}

/** The outcome line plus a per-kind breakdown when anything failed. */
function SyncOutcome({ lastRun }: { lastRun: HocPhiLastSync }) {
	const failures = (Object.entries(lastRun.failures ?? {}) as Array<[HocPhiFailureKind, number]>)
		.filter(([, count]) => count > 0)
		.sort((a, b) => b[1] - a[1]);

	return (
		<div className="space-y-2">
			{lastRun.message && (
				<p className="text-sm text-muted-foreground" title={lastRun.message}>
					{lastRun.message}
				</p>
			)}

			{lastRun.abortedBy && (
				<p className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
					<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
					Lượt chạy đã dừng sớm để không ghi đè học phí của những sinh viên chưa tra cứu được.
				</p>
			)}

			{failures.length > 0 && (
				<ul className="space-y-1">
					{failures.map(([kind, count]) => {
						const info = FAILURE_INFO[kind];
						return (
							<li key={kind} className="flex items-baseline gap-2 text-sm">
								<span
									className={`font-medium tabular-nums ${info.severe ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}
								>
									{count}
								</span>
								<span
									className={
										info.severe ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
									}
								>
									{info.label}
								</span>
								<span className="text-xs text-muted-foreground">— {info.hint}</span>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}

/** Last-run outcome, or the live state while a run is in flight. */
function SyncBadge({ status }: { status: HocPhiSyncStatus | null }) {
	if (status?.running) {
		return (
			<Badge variant="secondary" className="h-9 px-3 gap-2">
				<Loader2 className="h-3 w-3 animate-spin" />
				Đang chạy
			</Badge>
		);
	}

	const lastRun = status?.lastRun;
	if (!lastRun) {
		return <span className="text-sm text-muted-foreground">Chưa chạy lần nào</span>;
	}

	// A run recorded as still running while nothing is in flight means the
	// process died mid-run — reporting it as "done" would be a lie.
	if (lastRun.interrupted || lastRun.status === "running") {
		return (
			<Badge variant="outline" className="h-9 px-3 text-amber-700 dark:text-amber-400">
				Bị gián đoạn · {formatTime(lastRun.startedAt)}
			</Badge>
		);
	}

	const when = formatTime(lastRun.finishedAt ?? lastRun.startedAt);
	const how = TRIGGER_LABEL[lastRun.trigger] ?? lastRun.trigger;

	if (lastRun.status === "cancelled") {
		return (
			<Badge variant="outline" className="h-9 px-3">
				Đã dừng · {when} · {how}
			</Badge>
		);
	}

	return lastRun.status === "error" ? (
		<Badge variant="destructive" className="h-9 px-3">
			Lỗi · {when} · {how}
		</Badge>
	) : (
		<Badge variant="secondary" className="h-9 px-3">
			Hoàn tất · {when} · {how}
		</Badge>
	);
}
